/**
 * The dashboard itself: the page every other component in this folder is a part of.
 *
 * It owns the three pieces of state that more than one component needs and that
 * nothing else can own:
 *
 * - **the window**, because the overview, the table and the drill-down must all
 *   be describing the same slice of time. Two components each holding their own
 *   window would let the page show a 30m overview above a 7d table with nothing
 *   on screen saying so.
 * - **the status filter**, because the chips and the table are the same control
 *   split in two.
 * - **the selected endpoint**, because a row click in the table opens a panel
 *   that is not inside the table.
 *
 * Everything else is deliberately left to the children: each of them fetches
 * its own data through React Query, keyed by window, so switching windows
 * re-asks the API rather than relabelling numbers that were measured elsewhere.
 */
import { useEffect, useRef, useState } from 'react'

import { DEFAULT_WINDOW } from '../api/types'
import type { HealthState, WindowId } from '../api/types'
import { useI18n } from '../i18n'
import { AppHeader } from '../nav/AppHeader'
import { EndpointDetail } from './EndpointDetail'
import { EndpointTable } from './EndpointTable'
import { Overview, useOverviewQuery } from './Overview'
import { ALL_HEALTH_STATES, StatusFilter } from './StatusFilter'
import { WindowPicker } from './WindowPicker'

export function DashboardPage() {
  const { t } = useI18n()
  const [windowId, setWindowId] = useState<WindowId>(DEFAULT_WINDOW)
  const [statusFilter, setStatusFilter] = useState<ReadonlySet<HealthState>>(ALL_HEALTH_STATES)
  const [selectedEndpointId, setSelectedEndpointId] = useState<string | null>(null)

  // The drawer stays mounted and slides on a transform, so it needs the id to
  // survive the close animation — unmounting the content on click would empty
  // the panel before it had finished leaving the screen.
  const lastShownId = useRef<string | null>(null)
  if (selectedEndpointId !== null) lastShownId.current = selectedEndpointId
  const panelEndpointId = selectedEndpointId ?? lastShownId.current
  const panelOpen = selectedEndpointId !== null

  // Escape closes it. A slide-over that covers the page and can only be
  // dismissed by finding its button is a trap on a laptop trackpad.
  useEffect(() => {
    if (!panelOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedEndpointId(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [panelOpen])

  // Same query key as the <Overview/> below, so React Query hands back the
  // cached response rather than firing a second identical request. The chips
  // get their per-state totals for free.
  const { data: overview } = useOverviewQuery(windowId)

  return (
    <div className="min-h-full bg-bg">
      {/* Same bar as before — the title, the window picker, the account block.
          It moved into <AppHeader/> so the profile and admin pages get the
          identical one, and the picker rides along in its slot. */}
      <AppHeader>
        <WindowPicker value={windowId} onChange={setWindowId} />
      </AppHeader>

      <main className="mx-auto max-w-[110rem] space-y-6 px-4 py-6">
        <Overview window={windowId} />

        <StatusFilter
          selected={statusFilter}
          onChange={setStatusFilter}
          counts={overview?.health_counts}
        />

        <EndpointTable
          window={windowId}
          statusFilter={statusFilter}
          onSelectEndpoint={setSelectedEndpointId}
          selectedEndpointId={selectedEndpointId}
        />
      </main>

      {/*
        A slide-over, not a column beside the table. Sharing the row cost the
        table ~26rem of width and left the chart too narrow to read, and an
        ECharts tooltip inside a 26rem scroll container was clipped by it. As an
        overlay the panel gets half the viewport, the chart gets room, and the
        table underneath keeps its full width.

        Mounted at all times and moved with a transform: mounting on open would
        have nothing to animate from, and unmounting on close would blank the
        content mid-slide.
      */}
      <div
        aria-hidden={!panelOpen}
        onClick={() => setSelectedEndpointId(null)}
        className={`fixed inset-0 z-30 bg-fg/20 transition-opacity duration-200 ${
          panelOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />
      <aside
        aria-label={t('dashboard.page.detailPanel')}
        aria-hidden={!panelOpen}
        // `inert` keeps the closed panel out of the tab order; without it you
        // can tab into a drawer that is off-screen.
        {...(panelOpen ? {} : { inert: '' as unknown as boolean })}
        className={`fixed inset-y-0 right-0 z-40 flex w-full max-w-[52rem] flex-col
                    overflow-y-auto border-l border-border bg-surface p-6 shadow-2xl
                    transition-transform duration-200 ease-out ${
                      panelOpen ? 'translate-x-0' : 'translate-x-full'
                    }`}
      >
        {panelEndpointId !== null && (
          <EndpointDetail
            // Keyed by id so switching rows remounts rather than showing the
            // previous endpoint's numbers under the new endpoint's name.
            key={panelEndpointId}
            endpointId={panelEndpointId}
            window={windowId}
            onClose={() => setSelectedEndpointId(null)}
          />
        )}
      </aside>
    </div>
  )
}
